import { FC, useEffect, useState } from 'react'
import {
	Button,
	Card,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
	message
} from 'antd'
import { ReloadOutlined, RollbackOutlined } from '@ant-design/icons'
import {
	CarrierType,
	VersionRecord,
	fetchCarrierTypes,
	fetchVersions,
	rollback
} from '../api/updates'

const { Title, Text } = Typography
const { TextArea } = Input

const UpdatesVersions: FC = () => {
	const [versions, setVersions] = useState<VersionRecord[]>([])
	const [carrierTypes, setCarrierTypes] = useState<CarrierType[]>([])
	const [loading, setLoading] = useState(false)
	const [carrierTypeFilter, setCarrierTypeFilter] = useState<
		string | undefined
	>(undefined)
	const [rollbackTarget, setRollbackTarget] =
		useState<VersionRecord | null>(null)
	const [rollbackForm] = Form.useForm()
	const [rolling, setRolling] = useState(false)

	const load = async () => {
		setLoading(true)
		try {
			const data = await fetchVersions(carrierTypeFilter)
			setVersions(data)
		} catch {
			message.error('Failed to load versions')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchCarrierTypes()
			.then(setCarrierTypes)
			.catch(() => {})
	}, [])

	useEffect(() => {
		load()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [carrierTypeFilter])

	const openRollback = (record: VersionRecord) => {
		setRollbackTarget(record)
		rollbackForm.resetFields()
	}

	const handleRollback = async (values: { machineIds?: string }) => {
		if (!rollbackTarget) return
		setRolling(true)
		try {
			const machineIds = values.machineIds
				? values.machineIds
						.split(/[\n,]+/)
						.map((s) => s.trim())
						.filter(Boolean)
				: undefined
			await rollback({
				carrierType: rollbackTarget.carrierType,
				version: rollbackTarget.version,
				machineIds: machineIds?.length ? machineIds : undefined
			})
			message.success(
				`Rolled back to ${rollbackTarget.version} for ${rollbackTarget.carrierType}`
			)
			setRollbackTarget(null)
		} catch {
			message.error('Rollback failed')
		} finally {
			setRolling(false)
		}
	}

	const columns = [
		{
			title: 'Carrier Type',
			dataIndex: 'carrierType',
			key: 'carrierType',
			render: (slug: string) => <Tag>{slug}</Tag>
		},
		{
			title: 'Version',
			dataIndex: 'version',
			key: 'version',
			render: (v: string) => <strong>{v}</strong>
		},
		{
			title: 'Size',
			dataIndex: 'size',
			key: 'size',
			render: (n: number) =>
				n ? `${(n / 1024 / 1024).toFixed(2)} MB` : '—'
		},
		{
			title: 'Checksum',
			dataIndex: 'checksum',
			key: 'checksum',
			render: (sha: string) =>
				sha ? (
					<Tooltip title={sha}>
						<Text code style={{ fontSize: 11 }}>
							{sha.slice(0, 12)}…
						</Text>
					</Tooltip>
				) : (
					'—'
				)
		},
		{
			title: 'Release Notes',
			dataIndex: 'releaseNotes',
			key: 'releaseNotes',
			render: (notes: string) =>
				notes ? (
					<Tooltip title={notes}>
						<Text
							style={{
								maxWidth: 200,
								display: 'inline-block',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap'
							}}>
							{notes}
						</Text>
					</Tooltip>
				) : (
					<Text type="secondary">—</Text>
				)
		},
		{
			title: 'Release Date',
			dataIndex: 'releaseDate',
			key: 'releaseDate',
			render: (d: string) =>
				d ? (
					<Tooltip title={new Date(d).toISOString()}>
						{new Date(d).toLocaleString('ru')}
					</Tooltip>
				) : (
					'—'
				)
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: unknown, record: VersionRecord) => (
				<Button
					size="small"
					icon={<RollbackOutlined />}
					onClick={() => openRollback(record)}>
					Rollback to this
				</Button>
			)
		}
	]

	return (
		<div className="carriers">
			<header className="page-header">
				<Title level={4} style={{ margin: 0 }}>
					Versions
				</Title>
				<Button
					icon={<ReloadOutlined />}
					onClick={load}
					loading={loading}>
					Refresh
				</Button>
			</header>

			<main>
				<Card style={{ marginBottom: 16 }}>
					<Select
						allowClear
						placeholder="Filter by carrier type"
						value={carrierTypeFilter}
						onChange={setCarrierTypeFilter}
						style={{ width: 240 }}
						options={carrierTypes.map((ct) => ({
							value: ct.slug,
							label: `${ct.name} (${ct.slug})`
						}))}
					/>
				</Card>

				<Table
					dataSource={versions}
					columns={columns}
					rowKey={(r) => `${r.carrierType}-${r.version}`}
					loading={loading}
					pagination={{ pageSize: 50 }}
				/>
			</main>

			<Modal
				title={
					rollbackTarget
						? `Rollback ${rollbackTarget.carrierType} → ${rollbackTarget.version}`
						: 'Rollback'
				}
				open={!!rollbackTarget}
				onCancel={() => setRollbackTarget(null)}
				footer={null}
				destroyOnClose>
				{rollbackTarget && (
					<>
						<Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
							The "latest" pointer for{' '}
							<Tag>{rollbackTarget.carrierType}</Tag> will be set to{' '}
							<strong>{rollbackTarget.version}</strong>. On their next
							30-min check-in, machines with a newer version will
							downgrade.
						</Text>
						<Form
							form={rollbackForm}
							layout="vertical"
							onFinish={handleRollback}>
							<Form.Item
								label="Limit to specific machine IDs (optional)"
								name="machineIds"
								help="One machine ID per line or comma-separated. Leave empty to affect all machines of this carrier type.">
								<TextArea
									rows={4}
									placeholder="machine-001&#10;machine-002"
								/>
							</Form.Item>
							<Space
								style={{
									justifyContent: 'flex-end',
									width: '100%'
								}}>
								<Button onClick={() => setRollbackTarget(null)}>
									Cancel
								</Button>
								<Button
									type="primary"
									danger
									htmlType="submit"
									loading={rolling}
									icon={<RollbackOutlined />}>
									Confirm Rollback
								</Button>
							</Space>
						</Form>
					</>
				)}
			</Modal>
		</div>
	)
}

export default UpdatesVersions
